import { Bell, CircleHelp, FileCog, Mail, Menu, Moon, Settings, SlidersHorizontal, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { LanguageSwitch } from '@/components/language-switch';
import { FavoriteStar } from '@/features/favorites/favorite-star';
import { CONTACT_PATH, helpPathFor } from '@/features/help/help-links';
import { useNotificationSummary } from '@/features/notifications/api';
import { GlobalSearch } from '@/features/search/global-search';
import { usePersistentTheme } from '@/features/personalization/use-appearance';

const ICON_BUTTON =
  'flex size-10 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground';

/**
 * The top bar of §4.3.2: 64px (GEN-9's `h-16`, on every signed-in screen),
 * hairline underneath. Breadcrumb (the group, in
 * capitals — one line of text, not links: GEN-10), page title (GEN-11), the
 * search box, EN/ع, theme, preferences shortcut and the bell.
 *
 * GEN-15 / GEN-182 / NFR-37 / EC-19-54: every icon-only button carries an aria-label
 * and a title tooltip. PART 20.6's finding was that this shell had "not one
 * label for a screen reader… and exactly one tooltip", with the theme,
 * settings and notification buttons carrying no name at all — those three
 * are the ones below. EC-19-55's `Toggle theme` literal is
 * `t('shell.themeToggle')` now, translated with everything else. NFR-36 is the other half and is why each is a real <button> or a real
 * <Link> rather than a styled box: the bell is a Link, not a div with a
 * handler.
 * GEN-12's search is BUILT, and this comment used to be the note saying it was
 * not: the backend was deferred "until the first searchable records", the
 * records existed for weeks, and the box stayed inert — the shell's largest
 * remaining gap. It is now `<GlobalSearch />`, which owns the box and the
 * results panel together (GEN-12a permission-filtered results, GEN-12b open at
 * two characters and ask after 250 ms, GEN-12c keyboard operable, GEN-12d
 * every row carrying its code). The chassis was the product's `<Typeahead>`
 * (GEN-50) and the picker-search server shape, as predicted here.
 *
 * THE BELL (GEN-13, ADM-04 §3). It was an inert `<button>` with no handler for
 * the whole build — the defect EC-19-59 DESCRIBES, but not one of the controls
 * it LISTS. That row names ten, every one of them on an admin screen (New
 * custom role, Invite user, the per-user row menu, Add customer, Mark all as
 * read, every notification row, New key, Download template, Export now, Manage
 * payment method) under a headline that says "six"; the bell is on none of
 * them, and neither is the search box above. Those two were the ACCEPTANCE
 * REPORT's examples against the row — both on this very file, and both since
 * refuted — which is why the row is settled by the sweep in
 * `ec1959-control-census.test.ts` rather than by anybody's list of examples.
 * What carries here is the rule's reason, which is universal: "A button that
 * does nothing teaches a user that the product is broken."
 * It is now a LINK to `/notifications`
 * because BR-ADM-04-02 is explicit: "The bell must open the FULL PAGE, not a
 * drop-down panel", and a build must not make one.
 *
 * The dot follows BR-ADM-04-01 exactly: present when the signed-in person has
 * at least one unread notification, absent when they have none, AND NEVER A
 * NUMBER. The count is deliberately not drawn even though the summary carries
 * it — "It must not show a count" is the rule, and the reason is that a bell
 * is a prompt to look, not a metric.
 *
 * THE MENU BUTTON (D25, delegated ruling 26 Aug 2026). On a narrow window the
 * §4.3.1 menu column is not drawn and this bar carries the control that opens
 * it. `openMenu` is the whole switch: the shell passes it only on the narrow
 * frame, so on a desktop window the button does not exist — not hidden by a
 * stylesheet, ABSENT, which is the difference between a control a screen
 * reader cannot see and one it can still walk onto.
 */
export function Topbar({
  heading,
  openMenu,
}: {
  heading: { labelKey: string; pageKey: string; to: string };
  /** D25: supplied on the narrow frame only. Undefined means desktop, and
   *  desktop draws no button. */
  openMenu?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  // ADM-08: the same `appearance.theme` row the Personalization screen writes,
  // so the two controls cannot disagree and the choice follows the person to
  // another computer (BR-ADM-08-03).
  const { theme, toggle: toggleTheme } = usePersistentTheme();

  return (
    <header className="flex h-16 shrink-0 items-center gap-4 border-b border-border bg-card px-6">
      {/*
        D25's reveal, FIRST in the bar. Its position needs no direction logic:
        the bar is a flex row and the document's `dir` decides which end "first"
        is, so it sits at the leading edge — before the breadcrumb in English,
        and on the RIGHT in Arabic, where an Arabic reader begins (NFR-02).

        GEN-182 / NFR-37 / GEN-15: icon-only, so it carries both an accessible
        name and a tooltip, from one key, like every other control in this bar.
      */}
      {openMenu === undefined ? null : (
        <button
          type="button"
          onClick={openMenu}
          aria-label={t('shell.openMenu')}
          title={t('shell.openMenu')}
          className={`${ICON_BUTTON} -ms-2 shrink-0`}
        >
          <Menu className="size-5" aria-hidden />
        </button>
      )}
      {/* The breadcrumb and title are ONE link to the section's home, so the
          title is also the way back from any screen inside it. */}
      <Link to={heading.to} className="group min-w-0 rounded-md">
        <div className="truncate text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
          {t(heading.labelKey)}
        </div>
        <h1 className="truncate text-base font-bold leading-tight group-hover:underline">
          {t(heading.pageKey)}
        </h1>
      </Link>
      {/* G-01 / INV-H8 — the star that pins this screen to the favourites bar.
          Beside the title because the title is what it stores: the heading the
          person is reading (a document page's number) is the label. */}
      <FavoriteStar fallbackLabel={t(heading.pageKey)} />

      {/* §4.3.2 row 3 — "A recessed grey box up to 380px wide". What is inside
          it is GEN-12's own control now, box and panel together. */}
      <div className="ms-auto hidden w-full max-w-[380px] md:block">
        <GlobalSearch />
      </div>

      <div className="ms-auto flex items-center gap-1 md:ms-0">
        <LanguageSwitch />
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={t('shell.themeToggle')}
          title={theme === 'dark' ? t('shell.themeLight') : t('shell.themeDark')}
          className={ICON_BUTTON}
        >
          {/* GEN-14: a moon or a sun; clicking swaps light and dark.
              NFR-12 — the theme is switchable "from the top bar on any
              screen": the shell renders this bar on every routed page, and
              the other half of the rule (the appearance settings page) is the
              Theme card in personalization-page.tsx — both write the same
              appearance.theme row, so they cannot disagree. */}
          {theme === 'dark' ? <Sun className="size-4.5" /> : <Moon className="size-4.5" />}
        </button>
        {/*
          ADM-08 §3 — "The SLIDERS BUTTON in the top bar… works from any screen,
          for everybody, whatever their role." It pointed at `/settings`, which
          is General Settings and needs `admin` View: the majority of a
          company's staff clicked it and met a refusal. It now opens the screen
          it names, which has no permission at all (§9, BR-PERM-08).
        */}
        <Link
          to="/personalization"
          aria-label={t('shell.preferences')}
          title={t('shell.preferences')}
          className={ICON_BUTTON}
        >
          <SlidersHorizontal className="size-4.5" />
        </Link>
        {/* UB-03's four, on the WIDE frame only — the narrow bar has no room
            for them at 320px, so the slide-over menu carries them instead
            (nav-slide-over.tsx), on the same D25 switch as the menu button. */}
        {openMenu === undefined ? <TopbarQuickLinks /> : null}
        <NotificationBell />
      </div>
    </header>
  );
}

/**
 * UB-03 (bug-triage plan, Oct 2026) — "program settings, print options, help,
 * chat with us", as four icon-only controls beside the theme toggle.
 *
 *  - Settings opens General Settings (`/settings`). It needs `admin` View,
 *    and that is the screen the owner asked for; the sliders button above
 *    remains the everybody-route to the person's own preferences.
 *  - Print options opens the Printing screen (`/settings/printing`, INV-H6):
 *    the `print.*` company settings with a live preview. Until that screen
 *    existed the button pointed at Number sequences under a name that said
 *    so, because EC-19-59 forbids a button that opens nothing.
 *  - Help opens the in-app Help page (`features/help`), scrolled to the
 *    section for the module the person is standing in — `/help#sales` from
 *    any Sales screen, the page's top from Home or Settings. The base path is
 *    `HELP_URL` in packages/config; `helpPathFor` adds the section. Until the
 *    page existed this opened a placeholder docs address in a new tab.
 *  - Contact us opens the same page's contact footer (`/help#contact`), which
 *    carries the `mailto:` to `SUPPORT_EMAIL` and, once a number is listed,
 *    WhatsApp — from the same package.
 *
 * Exported because the narrow frame draws the same four inside the slide-over
 * menu rather than in the bar; one component, so the two cannot drift.
 */
export function TopbarQuickLinks() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  return (
    <>
      <Link to="/settings" aria-label={t('shell.settings')} title={t('shell.settings')} className={ICON_BUTTON}>
        <Settings className="size-4.5" aria-hidden />
      </Link>
      <Link
        to="/settings/printing"
        aria-label={t('shell.printOptions')}
        title={t('shell.printOptions')}
        className={ICON_BUTTON}
      >
        <FileCog className="size-4.5" aria-hidden />
      </Link>
      <Link
        to={helpPathFor(pathname)}
        aria-label={t('shell.help')}
        title={t('shell.help')}
        className={ICON_BUTTON}
      >
        <CircleHelp className="size-4.5" aria-hidden />
      </Link>
      <Link to={CONTACT_PATH} aria-label={t('shell.contact')} title={t('shell.contact')} className={ICON_BUTTON}>
        <Mail className="size-4.5" aria-hidden />
      </Link>
    </>
  );
}

/**
 * ADM-04 §3 — "a 40-pixel square carrying a `notifications` icon at the far
 * end. It carries a small red dot 7 pixels across near its top corner… It
 * works for everybody, whatever their role."
 *
 * Whatever their role is not a figure of speech: the route behind it is
 * `@SelfService`, so an account holding no role at all still has an inbox
 * (PART 16.1 rung 4, BR-PERM-73). The bell is therefore never hidden and never
 * greyed.
 */
function NotificationBell() {
  const { t } = useTranslation();
  const { data } = useNotificationSummary();
  const hasUnread = (data?.total ?? 0) > 0;

  return (
    <Link
      to="/notifications"
      aria-label={t('shell.notifications')}
      title={t('shell.notifications')}
      className={`${ICON_BUTTON} relative`}
    >
      <Bell className="size-4.5" />
      {hasUnread ? (
        <span
          // 7px, near the top corner, on the END side so it mirrors in Arabic
          // with the rest of the bar.
          className="absolute end-2 top-2 size-[7px] rounded-full bg-destructive"
          // A dot is not decoration here: it is the only signal, so it carries
          // a name (GEN-15) — and colour is never the only signal (§21.17).
          role="status"
          aria-label={t('notif.unreadDot')}
        />
      ) : null}
    </Link>
  );
}
